﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AttachEventToShopRequest,
  ShopEvent,
  UpdateShopEventMetadataRequest,
  UpdateShopEventTicketsRequest,
} from "@ticketnjoy/types";

import { apiClient } from "@/lib/api-client";

export const shopEventsQueryKey = (shopSlug: string) =>
  ["shops", shopSlug, "events"] as const;

export function useShopEventsQuery(shopSlug: string) {
  return useQuery<ShopEvent[]>({
    queryKey: shopEventsQueryKey(shopSlug),
    queryFn: () => apiClient<ShopEvent[]>(`/shops/${shopSlug}/events`),
    enabled: Boolean(shopSlug),
  });
}

export function useAttachEventMutation(shopSlug: string) {
  const qc = useQueryClient();
  return useMutation<ShopEvent, Error, AttachEventToShopRequest>({
    mutationFn: (body) =>
      apiClient<ShopEvent>(`/shops/${shopSlug}/events`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopEventsQueryKey(shopSlug) });
    },
  });
}

/**
 * Section ve display order günceller — görünürlüğe dokunmaz.
 * Show/hide için ayrı useShopEventVisibilityMutation kullan.
 */
export function useUpdateShopEventMetadataMutation(shopSlug: string, shopEventId: string) {
  const qc = useQueryClient();
  return useMutation<ShopEvent, Error, UpdateShopEventMetadataRequest>({
    mutationFn: (body) =>
      apiClient<ShopEvent>(`/shops/${shopSlug}/events/${shopEventId}`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopEventsQueryKey(shopSlug) });
    },
  });
}

/**
 * Listed ↔ Hidden flip. Body yok; endpoint state'i taşır.
 */
export function useShopEventVisibilityMutation(shopSlug: string, shopEventId: string) {
  const qc = useQueryClient();
  return useMutation<ShopEvent, Error, "show" | "hide">({
    mutationFn: (action) =>
      apiClient<ShopEvent>(`/shops/${shopSlug}/events/${shopEventId}/${action}`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopEventsQueryKey(shopSlug) });
    },
  });
}

export function useUpdateShopEventTicketsMutation(shopSlug: string, shopEventId: string) {
  const qc = useQueryClient();
  return useMutation<ShopEvent, Error, UpdateShopEventTicketsRequest>({
    mutationFn: (body) =>
      apiClient<ShopEvent>(`/shops/${shopSlug}/events/${shopEventId}/tickets`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopEventsQueryKey(shopSlug) });
    },
  });
}

export function useDetachEventMutation(shopSlug: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (shopEventId) =>
      apiClient<void>(`/shops/${shopSlug}/events/${shopEventId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shopEventsQueryKey(shopSlug) });
    },
  });
}
